import { useEffect, useState } from 'react';
import { Container, Nav, Button, Offcanvas } from 'react-bootstrap';
import { useAuth, useClerk, useUser } from '@clerk/react';
import { setTokenGetter } from './api/Api';
import { FuelPlanner } from './components/planner';
import LandingPage from './components/LandingPage';
import AboutTab from './components/landing/AboutTab';
import LoreTab from './components/landing/LoreTab';
import ScienceTab from './components/ScienceTab';
import AccountSettings from './components/AccountSettings';
import AccountProfile from './components/AccountProfile';
import { useLocaleContext } from './i18n';
import { BrandName } from './components/BrandName';
import './styles/app.css';

type Tab = 'planner' | 'settings' | 'account' | 'science' | 'about' | 'lore' | 'logout';


export default function AppContent() {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { user } = useUser();
  const { lang, setLang, locale } = useLocaleContext();
  const [showMobileNav, setShowMobileNav] = useState(typeof window !== 'undefined' && window.innerWidth < 992);
  const [activeTab, setActiveTab] = useState<Tab>('planner');
  const [langInitialized, setLangInitialized] = useState(false);
  const { signOut } = useClerk();

  useEffect(() => {
    setTokenGetter(() => getToken({ template: 'broomwagon-api' }));
  }, [getToken]);

  useEffect(() => {
    if (!langInitialized && user?.unsafeMetadata) {
      const meta = user.unsafeMetadata as { lang?: string };
      if (meta.lang === 'en' || meta.lang === 'pl') {
        setLang(meta.lang);
      }
      setLangInitialized(true);
    }
  }, [user?.unsafeMetadata, langInitialized, setLang]);

  if (!isLoaded) return null;

  if (!isSignedIn) {
    return <LandingPage />;
  }

  const handleClose = () => setShowMobileNav(false);
  const handleShow = () => setShowMobileNav(true);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    // Close menu only on mobile
    if (window.innerWidth < 992) {
      setShowMobileNav(false);
    }
  };

  const NavigationContent = () => (
    <>
      <Nav className="flex-column flex-grow-1 px-2 gap-1">
        <Nav.Link
          active={activeTab === 'planner'}
          onClick={() => switchTab('planner')}
          className={`rounded px-3 py-2 mb-1 ${activeTab === 'planner' ? 'bg-primary text-white' : 'text-secondary'}`}
        >
          <i className="bi bi-calendar-range me-2"></i> {locale.nav.planner}
        </Nav.Link>

        <Nav.Link
          active={activeTab === 'settings'}
          onClick={() => switchTab('settings')}
          className={`rounded px-3 py-2 mb-1 ${activeTab === 'settings' ? 'bg-primary text-white' : 'text-secondary'}`}
        >
          <i className="bi bi-sliders me-2"></i> {locale.nav.settings}
        </Nav.Link>

        <Nav.Link
          active={activeTab === 'account'}
          onClick={() => switchTab('account')}
          className={`rounded px-3 py-2 mb-1 ${activeTab === 'account' ? 'bg-primary text-white' : 'text-secondary'}`}
        >
          <i className="bi bi-person-circle me-2"></i> {locale.nav.account}
        </Nav.Link>

        <Nav.Link
          active={activeTab === 'science'}
          onClick={() => switchTab('science')}
          className={`rounded px-3 py-2 mt-2 ${activeTab === 'science' ? 'bg-primary text-white' : 'text-secondary'}`}
        >
          <i className="bi bi-book me-2"></i> {locale.tabs.science}
        </Nav.Link>

        <Nav.Link
          active={activeTab === 'lore'}
          onClick={() => switchTab('lore')}
          className={`rounded px-3 py-2 ${activeTab === 'lore' ? 'bg-primary text-white' : 'text-secondary'}`}
        >
          <i className="bi bi-bookmark-fill me-2"></i> {locale.tabs.broomWagon}
        </Nav.Link>
        <Nav.Link
          active={activeTab === 'about'}
          onClick={() => switchTab('about')}
          className={`rounded px-3 py-2 ${activeTab === 'about' ? 'bg-primary text-white' : 'text-secondary'}`}
        >
          <i className="bi bi-info-circle me-2"></i> {locale.nav.about}
        </Nav.Link>
      </Nav>

      <div className="mt-auto pt-3 px-2" style={{ borderTop: '1px solid #2a2a2a' }}>
        <Nav.Link
          active={activeTab === 'logout'}
          onClick={() => signOut()}
          className={`rounded px-3 py-2 ${activeTab === 'logout' ? 'bg-primary text-white' : 'text-secondary'}`}
        >
          <i className="bi bi-box-arrow-right me-2"></i> {locale.nav.logout}
        </Nav.Link>
      </div>
    </>
  );

  return (
    <div data-bs-theme="dark" className="broomwagon-app">
      {/* Desktop Sidebar */}
      <aside className="broomwagon-sidebar d-none d-lg-flex flex-column">
        <NavigationContent />
      </aside>

      {/* Mobile Hamburger Menu */}
      <div className="broomwagon-topbar d-lg-none">
        <Button variant="link" className="text-light p-0" onClick={handleShow} style={{ fontSize: '1.2rem', lineHeight: 1 }}>
          <i className="bi bi-list"></i>
        </Button>
      </div>

      {/* Mobile Navigation (Offcanvas) */}
      <Offcanvas show={showMobileNav} onHide={handleClose} className="bg-dark text-light border-end border-secondary d-lg-none" style={{ width: '280px' }}>
        <Offcanvas.Header closeButton closeVariant="white">
          <Offcanvas.Title><BrandName /></Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column p-0">
          <NavigationContent />
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main Content Area */}
      <div className="broomwagon-main-wrapper">
        <main className="broomwagon-main">
          <Container fluid="lg" className="pt-3 pb-4">
             <div style={{ display: activeTab === 'planner' ? 'block' : 'none' }}>
               <FuelPlanner />
             </div>
             <div style={{ display: activeTab === 'settings' ? 'block' : 'none' }}>
               <AccountSettings locale={locale} lang={lang} setLang={setLang} />
             </div>
             <div style={{ display: activeTab === 'account' ? 'block' : 'none' }}>
               <AccountProfile locale={locale} />
             </div>
             <div style={{ display: activeTab === 'about' ? 'block' : 'none' }}>
               <AboutTab about={locale.about} support={locale.support} />
             </div>
             <div style={{ display: activeTab === 'lore' ? 'block' : 'none' }}>
               <LoreTab lore={locale.lore} />
             </div>
             <div style={{ display: activeTab === 'science' ? 'block' : 'none' }}>
               <ScienceTab science={locale.science} />
             </div>
          </Container>
        </main>
      </div>
    </div>
  );
}
